import {
  leadsImportListQueryKey,
  leadsImportPartialUpdateMutation,
  leadsImportRetrieveOptions,
  leadsImportUniqueFieldsRetrieveOptions,
  StepEnum
} from '@crm/api'
import {
  DuplicateFieldsBlock,
  DuplicatesModal
} from '@crm/features/leads/components'
import { useImportLeads } from '@crm/features/leads/contexts'
import { buildFields, toggleField } from '@crm/features/leads/utils'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useIdParam } from '@crm/hooks/useIdParam'
import { paths } from '@crm/routes'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Col, Form, message, Row } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { useEffect, useMemo, useState } from 'react'

export type FieldProps = {
  name: string
  column_index: number
}
export type UniqueFields = FieldProps[]
export type ImportHeader = {
  key?: string
  column_index: number
}

export const CheckDuplicates = () => {
  const { t } = useLocales()
  const { id } = useIdParam()
  const [form] = Form.useForm()
  const navigate = useAppNavigate()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { isPending, setIsPending } = useImportLeads()

  const [duplicatesData, setDuplicatesData] = useState<AnyObject[]>([])
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState<UniqueFields>([])
  const [selectedContact, setSelectedContact] = useState<UniqueFields>([])
  const [errors, setErrors] = useState<{
    [key: string]: { name: string | string[]; errors: string[] }
  }>()

  const { data: leadImport, isLoading: isLoadingImport } = useQuery({
    ...leadsImportRetrieveOptions({ path: { id: id } }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  const { data: uniqueField } = useQuery({
    ...leadsImportUniqueFieldsRetrieveOptions()
  })

  const { leadFields, contactFields } = useMemo(() => {
    if (!uniqueField || !leadImport) {
      return { leadFields: [], contactFields: [] }
    }

    return {
      leadFields: buildFields(
        uniqueField.lead_unique_fields,
        leadImport.headers?.lead_headers
      ),
      contactFields: buildFields(
        uniqueField.contact_unique_fields,
        leadImport.headers?.contact_headers
      )
    }
  }, [uniqueField, leadImport])

  useEffect(() => {
    if (!leadImport && !isLoadingImport) {
      message.error(t('import.error_load_import_data'))

      return
    }

    const allowed = leadImport?.unique_verify ?? []

    setSelectedLead(leadFields.filter((f) => allowed.includes(f.name)))
    setSelectedContact(contactFields.filter((f) => allowed.includes(f.name)))
  }, [leadImport, leadFields, contactFields, isLoadingImport, t])

  const leadSelectedNames = useMemo(
    () => new Set(selectedLead.map((f) => f.name)),
    [selectedLead]
  )

  const contactSelectedNames = useMemo(
    () => new Set(selectedContact.map((f) => f.name)),
    [selectedContact]
  )

  const leadCheck = leadFields.filter((f) => !leadSelectedNames.has(f.name))
  const contactCheck = contactFields.filter(
    (f) => !contactSelectedNames.has(f.name)
  )

  const onToggleLead = (name: string) => {
    setErrors(undefined)
    setSelectedLead((prev) => toggleField(name, leadFields, prev))
  }

  const onToggleContact = (name: string) => {
    setErrors(undefined)
    setSelectedContact((prev) => toggleField(name, contactFields, prev))
  }

  const { mutate: checkDuplicates } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: ({ duplicates }) => {
      const duplicatesArray = Array.isArray(duplicates) ? duplicates : []
      setDuplicatesData(duplicatesArray)

      if (duplicatesArray.length > 0) {
        setIsOpenModal(true)
        setIsPending(false)
      } else {
        goNextStep({
          path: { id: id },
          body: { step: StepEnum.CHECK_FOR_POSSIBLE_DUPLICATES }
        })
      }
    },
    onError: (error) => showApiErrors(error, form),
    onSettled: () => setIsPending(false)
  })

  const { mutate: goNextStep } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: (response) => {
      invalidateQueries(leadsImportListQueryKey())
      navigate(
        paths.leadImport.item(
          response.id.toString(),
          response.step?.toString() || ''
        )
      )
    },
    onError: (error) => showApiErrors(error, form),
    onSettled: () => setIsPending(false)
  })

  const onSubmit = () => {
    setIsPending(true)
    checkDuplicates({
      path: { id: id },
      body: {
        unique_verify: [...selectedLead, ...selectedContact].map((f) => f.name),
        step: StepEnum.CHECK_FOR_POSSIBLE_DUPLICATES
      }
    })
  }

  const onConfirm = () => {
    setIsPending(true)
    setIsOpenModal(false)
    goNextStep({
      path: { id: id },
      body: { step: StepEnum.CHECK_FOR_POSSIBLE_DUPLICATES }
    })
  }

  const onCancel = () => setIsOpenModal(false)

  return (
    <>
      <Text weight={500} size={18}>
        {t('import.description_check_for_duplicates')}
      </Text>

      <Form
        onFinish={onSubmit}
        form={form}
        name={`form-${StepEnum.CHECK_FOR_POSSIBLE_DUPLICATES}`}
        className="mt-20"
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <DuplicateFieldsBlock
              title={t('import.lead_duplicate_check')}
              checkList={leadCheck}
              selectedList={selectedLead}
              onToggle={onToggleLead}
            />
          </Col>

          <Col span={12}>
            <DuplicateFieldsBlock
              title={t('import.contact_duplicate_check')}
              checkList={contactCheck}
              selectedList={selectedContact}
              onToggle={onToggleContact}
            />
          </Col>
        </Row>

        {Array.isArray(errors?.unique_verify?.errors) && (
          <Alert
            message={errors.unique_verify.errors.join(', ')}
            type="error"
            className="mt-5"
          />
        )}

        <DuplicatesModal
          open={isOpenModal}
          onConfirm={onConfirm}
          onCancel={onCancel}
          loading={isPending}
          data={duplicatesData}
        />
      </Form>
    </>
  )
}
